import { useEffect, useState, type JSX } from 'react'
import { Shield, Smartphone, AlertTriangle, RefreshCcw, RotateCcw } from 'lucide-react'
import {
  getMfaStatus,
  setupMfa,
  verifyMfaSetup,
  enrollBackupCodes,
  disableMfa,
  resetMfa,
  getPlatformMfaStatus,
  setupPlatformMfa,
  verifyPlatformMfaSetup,
  enrollPlatformBackupCodes,
  disablePlatformMfa,
  resetPlatformMfa,
  type MfaSetup,
} from '../../api/mfa.js'
import { MfaSetupDialog } from './MfaSetupDialog.js'
import { MfaBackupCodesDialog } from './MfaBackupCodesDialog.js'
import { MfaDisableDialog } from './MfaDisableDialog.js'
import { MfaResetSelfDialog } from './MfaResetSelfDialog.js'

interface MfaSectionProps {
  mode?: 'tenant' | 'platform'
}

const R = 'rounded-[3px]'
const BTN_PRIMARY = `flex h-9 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`
const BTN_SECONDARY = `flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`
const BTN_DANGER = `flex h-9 items-center gap-1.5 ${R} border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40`

function StatusBadge({ enabled }: { enabled: boolean | null }): JSX.Element {
  if (enabled === null) {
    return <span className="text-xs text-slate-400">—</span>
  }

  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Enabled
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      Not enabled
    </span>
  )
}

export default function MfaSection({ mode = 'tenant' }: MfaSectionProps): JSX.Element {
  const isPlatform = mode === 'platform'
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null)
  const [mfaRequired, setMfaRequired] = useState<boolean>(false)
  const [canDisable, setCanDisable] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'setup' | 'backup' | 'disable' | 'reset' | null>(null)
  const [setup, setSetup] = useState<MfaSetup | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)

  useEffect(() => {
    setLoading(true)
    const statusFn = isPlatform ? getPlatformMfaStatus : getMfaStatus
    statusFn()
      .then((res) => {
        setMfaEnabled(res.mfaEnabled)
        setMfaRequired(res.mfaRequired ?? isPlatform)
        setCanDisable(res.canDisable === true)
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load MFA status'),
      )
      .finally(() => setLoading(false))
  }, [isPlatform])

  async function handleEnable(): Promise<void> {
    setError(null)
    setLoading(true)
    try {
      const res = isPlatform ? await setupPlatformMfa() : await setupMfa()
      setSetup(res)
      setModal('setup')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start MFA setup')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerify(code: string): Promise<void> {
    setError(null)
    setLoading(true)
    try {
      if (isPlatform) {
        const res = await verifyPlatformMfaSetup(code)
        const codes = (res as { codes?: string[] }).codes
        if (codes && codes.length > 0) {
          setBackupCodes(codes)
          setModal('backup')
        } else {
          setModal(null)
        }
        setMfaEnabled(true)
        setSetup(null)
      } else {
        await verifyMfaSetup(code)
        const res = await enrollBackupCodes()
        setBackupCodes(res.codes)
        setMfaEnabled(true)
        setSetup(null)
        setModal('backup')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code')
    } finally {
      setLoading(false)
    }
  }

  async function handleRegenerateBackupCodes(): Promise<void> {
    setError(null)
    setLoading(true)
    try {
      const res = isPlatform ? await enrollPlatformBackupCodes() : await enrollBackupCodes()
      setBackupCodes(res.codes)
      setModal('backup')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate backup codes')
    } finally {
      setLoading(false)
    }
  }

  async function handleReset(password: string): Promise<void> {
    setError(null)
    setLoading(true)
    try {
      if (isPlatform) {
        await resetPlatformMfa(password)
      } else {
        await resetMfa(password)
      }
      setMfaEnabled(false)
      setModal(null)
      // Immediately initiate setup so the user can re-enroll their new authenticator right away
      await handleEnable()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset MFA')
    } finally {
      setLoading(false)
    }
  }

  async function handleDisable(password: string): Promise<void> {
    setError(null)
    setLoading(true)
    try {
      if (isPlatform) {
        await disablePlatformMfa(password)
      } else {
        await disableMfa(password)
      }
      setMfaEnabled(false)
      setModal(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable MFA')
    } finally {
      setLoading(false)
    }
  }

  function closeModal(): void {
    setModal(null)
    setSetup(null)
    setBackupCodes(null)
    setError(null)
  }

  return (
    <>
      <section className={`border border-slate-200 bg-white p-5 ${R}`}>
        <div className="flex items-start gap-4">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}
          >
            <Shield className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
              Multi-Factor Authentication
            </h3>
            <p className="mt-1 text-[12.5px] text-slate-500">
              Add an extra layer of security to your account using an authenticator app.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {mfaRequired && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                Required
              </span>
            )}
            <StatusBadge enabled={mfaEnabled} />
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        {mfaEnabled === null || loading ? (
          <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
            <RefreshCcw className="h-4 w-4 animate-spin" />
            Loading MFA status…
          </div>
        ) : mfaEnabled ? (
          <div className="mt-5 space-y-3">
            <p className="text-xs text-slate-500">
              Multi-factor authentication is active on your account. You can generate fresh recovery backup codes or reset your authenticator app by verifying your password.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={BTN_PRIMARY}
                onClick={handleRegenerateBackupCodes}
                disabled={loading}
              >
                <RefreshCcw className="h-4 w-4" />
                Regenerate backup codes
              </button>

              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() => setModal('reset')}
                disabled={loading}
              >
                <RotateCcw className="h-4 w-4 text-blue-600" />
                Reset MFA
              </button>

              {canDisable && (
                <button
                  type="button"
                  className={BTN_DANGER}
                  onClick={() => setModal('disable')}
                  disabled={loading}
                >
                  <AlertTriangle className="h-4 w-4" />
                  Disable MFA
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {mfaRequired && (
              <p className="text-xs font-medium text-amber-700">
                Action required: Multi-factor authentication is mandatory for your account. Please set it up now.
              </p>
            )}
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={handleEnable}
              disabled={loading}
            >
              <Smartphone className="h-4 w-4" />
              Set Up MFA
            </button>
          </div>
        )}
      </section>

      {modal === 'setup' && setup && (
        <MfaSetupDialog
          setup={setup}
          onVerify={handleVerify}
          onCancel={closeModal}
          loading={loading}
          error={error}
        />
      )}

      {modal === 'backup' && backupCodes && (
        <MfaBackupCodesDialog codes={backupCodes} onClose={closeModal} />
      )}

      {modal === 'reset' && (
        <MfaResetSelfDialog
          onReset={handleReset}
          onCancel={closeModal}
          loading={loading}
          error={error}
        />
      )}

      {modal === 'disable' && (
        <MfaDisableDialog
          onDisable={handleDisable}
          onCancel={closeModal}
          loading={loading}
          error={error}
        />
      )}
    </>
  )
}
